import { parseSearchWith, stringifySearchWith } from '@tanstack/react-router';
import { datePresets } from '@qronstat/shared';
import type { DatePreset, DateRangeSpec } from '@qronstat/shared';

const isoDay = /^\d{4}-\d{2}-\d{2}$/;

export function encodePeriod(spec: DateRangeSpec): string {
  if ('preset' in spec) return spec.preset;
  if ('lastDays' in spec) return `last${spec.lastDays}d`;
  if ('since' in spec) return `${spec.since}..`;
  return spec.from === spec.to ? spec.from : `${spec.from}..${spec.to}`;
}

export function decodePeriod(value: string): DateRangeSpec | null {
  if ((datePresets as readonly string[]).includes(value)) return { preset: value as DatePreset };
  const last = /^last(\d{1,4})d$/.exec(value);
  if (last) return { lastDays: Number(last[1]) };
  if (isoDay.test(value)) return { from: value, to: value };
  const since = /^(\d{4}-\d{2}-\d{2})\.\.$/.exec(value);
  if (since?.[1]) return { since: since[1] };
  const [from, to] = value.split('..');
  if (from && to && isoDay.test(from) && isoDay.test(to)) return { from, to };
  return null;
}

function isPeriodObject(value: unknown): value is DateRangeSpec {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    ('preset' in (value as object) ||
      'from' in (value as object) ||
      'lastDays' in (value as object) ||
      'since' in (value as object))
  );
}

const parseBase = parseSearchWith(JSON.parse);
const stringifyBase = stringifySearchWith(JSON.stringify);

export function parseSearch(search: string): Record<string, unknown> {
  const out = parseBase(search) as Record<string, unknown>;
  if (typeof out.period === 'string') {
    const decoded = decodePeriod(out.period);
    if (decoded) out.period = decoded;
  }
  return out;
}

export function stringifySearch(search: Record<string, unknown>): string {
  return stringifyBase(
    isPeriodObject(search.period) ? { ...search, period: encodePeriod(search.period) } : search,
  );
}
